import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { Box, SelectProps, SxProps, Theme } from '@mui/material';
import ClearSelection from 'components/clear-selection';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { useAccountState } from 'contexts/account/account-context';
import _ from 'underscore';
import { normalizeName } from 'utils/list-sort';
import { useSitesState } from 'views/order-feed/context/sites-context';
import { useSelectedSite } from 'views/order-feed/features/my-supply-plan/hooks/use-selected-site';
import { Site } from 'views/order-feed/types/site.type';

type Props = {
  dark?: boolean;
  sx?: SxProps<Theme> | undefined;
} & SelectProps;

const SelectSite: React.FC<Props> = ({ label, dark, fullWidth, sx, ...props }) => {
  const { selectedSiteId } = useSelectedSite();
  const history = useHistory();
  const {
    account: { company },
  } = useAccountState();
  const {
    sitesQuery: { isLoading, data: sites },
  } = useSitesState();

  // #region STATE

  const [siteSearchValue, setSiteSearchValue] = useState<string>('');
  const [siteName, setSiteName] = useState<string>('');

  // #endregion STATE

  const filteredSites = useMemo(() => {
    return _.chain(sites)
      .filter((site) => site.name.toLowerCase().includes(siteSearchValue.toLowerCase()))
      .sortBy((site) => normalizeName(site.name))
      .value();
  }, [sites, siteSearchValue]);

  const handleSiteSelect = (site: Site) => {
    setSiteName(site.name);

    if (company?.features.hasOrderFreeDelivery2 && site.isOfd2Enabled) {
      history.push(`/playground/supply-plan/order-free-delivery/${site.siteErpId}/${site.globalSiteId}/orders`);
    } else history.push(`/playground/supply-plan/${site.siteErpId}/${site.globalSiteId}`);
  };

  useEffect(() => {
    if (!!selectedSiteId) {
      let site = filteredSites.find((s) => s.globalSiteId === selectedSiteId.split('/')[0]);
      setSiteName(site?.name || '');
    } else setSiteName('');
  }, [selectedSiteId, sites, filteredSites]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Select
        {...props}
        sx={sx}
        isLoading={isLoading}
        tooltipTitle={siteName}
        label={'Select Site'}
        value={selectedSiteId}
        search={{
          value: siteSearchValue,
          onChange: setSiteSearchValue,
        }}
        dark={dark}
        onOpen={() => {
          setSiteSearchValue('');
        }}
        onChange={(event) => {}}>
        {filteredSites.map((site) => (
          <RadioSelectItem
            key={site.globalSiteId}
            value={`${site.siteErpId}/${site.globalSiteId}`}
            onClick={() => {
              handleSiteSelect(site);
            }}
            selected={site.globalSiteId === selectedSiteId}>
            {site.name}
          </RadioSelectItem>
        ))}
      </Select>
      {!!selectedSiteId && (
        <ClearSelection
          sx={{
            justifyContent: 'flex-end',
          }}
          onClick={() => history.push(`/playground/supply-plan/sites`)}
        />
      )}
    </Box>
  );
};

export default SelectSite;
